import { Box, IconButton, Stack } from '@mui/material';
import {
  FilterKeys,
  GetGroupsQueryFilters,
  GroupMin,
  GroupType,
  useGetGroupsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { FilterButton, SearchInput } from '@schooly/components/filters';
import { MINI_LIST } from '@schooly/constants/test-ids';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { CrossIcon, FilterIcon, Loading, ModalContent } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { NoSearchResultsFound } from '../../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { useTimetable } from '../../../../context/timetable/WithTimetable';
import { TimetableGroup } from '../types';
import { normalizeGroup } from '../utils';
import { TimetableGroupCard } from './AddGroupModal';
import { TimetableGroupFilterPopup, TimetableGroupFilters } from './TimetableGroupFilterPopup';
import { TimetableInlineFiltersTags } from './TimetableInlineFiltersTags';

type TimetableAddGroupSidebarProps = {
  defaultFilters: GetGroupsQueryFilters;
  selectedGroups: TimetableGroup[];
  onSelectGroups: (groups: TimetableGroup[]) => void;
};

const PAGE_SIZE = 20;

export const TimetableAddGroupSidebar: FC<TimetableAddGroupSidebarProps> = ({
  defaultFilters,
  selectedGroups,
  onSelectGroups,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { structure } = useTimetable();
  const availableAgeGroupIds = structure?.age_group_ids ?? [];
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const { data, isLoading, params, isFetchingNextPage, hasNextPage, fetchNextPage, setParams } =
    useGetGroupsQuery(
      {
        schoolId,
        pageSize: PAGE_SIZE,
        filters: defaultFilters,
        min: true,
      },
      { refetchOnMount: 'always', enabled: !!schoolId },
    );

  const isTutorGroup = !!params.filters?.only_tutor_groups?.length;

  const handleSetFiltersQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const handleSetUserFilters = useCallback(
    (userFilters: TimetableGroupFilters) => {
      setParams((p) => ({
        ...p,
        filters: {
          ...defaultFilters,
          ...userFilters,
        },
      }));
    },
    [setParams, defaultFilters],
  );

  const loaderRef = useInfiniteScroll(isLoading || isFetchingNextPage, fetchNextPage);

  const selectedGroupIds = useMemo(
    () => new Set(selectedGroups.map((g) => g.id)),
    [selectedGroups],
  );

  const groups = useMemo(
    () =>
      data?.pages.reduce<(GroupMin & { group_type: GroupType })[]>(
        (prev, curr) => [
          ...prev,
          ...curr.results.filter(
            (g): g is GroupMin & { group_type: GroupType } =>
              !selectedGroupIds.has(g.id) && g.group_type !== undefined,
          ),
        ],
        [],
      ) ?? [],
    [data?.pages, selectedGroupIds],
  );

  const onCardClick = useCallback(
    (group: TimetableGroup) => {
      onSelectGroups([group]);
    },
    [onSelectGroups],
  );

  const userFilters: TimetableGroupFilters = {
    [FilterKeys.AgeGroup]: params.filters[FilterKeys.AgeGroup] ?? [],
    [FilterKeys.Subject]: params.filters[FilterKeys.Subject] ?? [],
    [FilterKeys.OnlyTutorGroups]: params.filters[FilterKeys.OnlyTutorGroups] ?? [],
  };

  const hasFiltersApplied = !!userFilters.subject?.length || !!userFilters.age_group?.length;

  const renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (!groups?.length) return <NoSearchResultsFound type="small" />;

    return (
      <Box>
        {groups?.map((group) => {
          const normalizedGroup = normalizeGroup(group);
          return (
            <TimetableGroupCard
              key={group.id}
              group={normalizedGroup}
              onClick={() => onCardClick(normalizedGroup)}
            />
          );
        })}

        {hasNextPage && (
          <>
            {isFetchingNextPage && (
              <Box sx={{ pt: 2 }}>
                <Loading />
              </Box>
            )}
            <div ref={loaderRef} />
          </>
        )}
      </Box>
    );
  };

  return (
    <Stack data-test-id={MINI_LIST.CONTAINER} sx={{ height: '100%' }}>
      <ModalHeader title={formatMessage({ id: 'section-Groups' })} active />
      <Box px={2.5} pt={2.5}>
        <Stack gap={1}>
          <Stack justifyContent="center" position="relative">
            <SearchInput
              placeholder={formatMessage({ id: 'groups-SearchGroups' })}
              onChangeText={handleSetFiltersQuery}
              value={params.query || ''}
              InputProps={{
                endAdornment: (
                  <Stack pl={0.75} gap={0.5} pr={1} flexDirection="row">
                    {!!params.query && (
                      <IconButton
                        sx={(theme) => ({ '&:hover': { color: theme.palette.text.primary } })}
                        inverse
                        onClick={() => handleSetFiltersQuery('')}
                      >
                        <CrossIcon />
                      </IconButton>
                    )}
                    <FilterButton
                      sx={(theme) => ({
                        '.svg-icon path': {
                          fill: hasFiltersApplied ? 'unset' : theme.palette.common.grey,
                        },
                        '&:hover .svg-icon path': {
                          fill: theme.palette.primary.main,
                        },
                      })}
                      onClick={openFilterModal}
                      hasFiltersApplied={hasFiltersApplied}
                    >
                      <FilterIcon />
                    </FilterButton>
                  </Stack>
                ),
              }}
            />
          </Stack>
          <TimetableInlineFiltersTags onOpenFilters={openFilterModal} filters={userFilters} />
        </Stack>
        {filtersModalVisible && (
          <TimetableGroupFilterPopup
            onClose={closeFiltersModal}
            onSetFilters={handleSetUserFilters}
            filters={userFilters}
            availableAgeGroupIds={availableAgeGroupIds}
            showSubjectFilter={!isTutorGroup}
          />
        )}
      </Box>
      <ModalContent active>{renderContent()}</ModalContent>
    </Stack>
  );
};
